'use client';
import {
  applyDepositDiscountCodeAction,
  applySpread4MonthsPlanAction,
  saveQuoteCart,
} from '@shared/components/cart/actions';
import BDButton from '@shared/components/elements/button';
import { Spinner } from '@shared/components/layout/spinner';

import { getCartDiscountsTotal, getCartLevelDiscounts } from '@shared/lib/cart/server-cart';
import {
  DIAMOND_DUE_RATIO,
  ORDER_TYPE_CONFIRMATION,
  ORDER_TYPE_SERVICE,
} from '@shared/lib/constants';
import { CartItem } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import {
  getCartOrderType,
  hasDepositDiscountCode,
  hasSpread4MonthsSellingPlan,
  isCartEmpty,
  isFirstOrderCart,
} from '@shared/lib/utils/cart-utils';
import { renderDiscount } from '@shared/lib/utils/discount-utils';
import { rPercentage } from '@shared/lib/utils/formats';
import { renderMoney } from '@shared/lib/utils/money';
import { useSession } from 'next-auth/react';
import React, { useActionState, useEffect, useState } from 'react';
import { PayIn2Plan, PayIn2PlanRaw, PayIn4Plan, PayIn4PlanRaw } from '../policy';

// Stay authenticated from Headless storefront to Checkout
// Note: New Customer Accounts must be enabled.

// In order to stay authenticated from the headless storefront to checkout, you need to add a query parameter logged_in=true to the end of the checkout url.

// For example, if the checkout url is https://<shop-domain>/checkouts/<checkout_id>, then the URL should look like this:

// https://<shop-domain>/checkouts/<checkout_id>?logged_in=true

const CartLineBreakdown = ({ line }: { line: CartItem }) => {
  return (
    <li key={line.id}>
      <ul>
        <li key={line.merchandise.id} className="flex w-full justify-between gap-2">
          <div className="flex w-auto flex-col">
            <p className="pr-1 text-sm font-normal">
              {line.merchandise.product.title} {line.quantity > 1 && `× ${line.quantity}`}
            </p>
            <p className="flex flex-row flex-wrap">
              {line.merchandise.selectedOptions.map((option) => (
                <span
                  className="border-bd-dark/80 not-first:before:content-['•'] px-1 before:pr-2"
                  key={option.value}
                >
                  {option.value}
                </span>
              ))}
            </p>
          </div>
          <p className="w-auto text-right">{renderMoney(line.cost.subtotalAmount)}</p>
        </li>
        {line.discountAllocations.map((discount) => {
          const { title, code, discountedAmount } = discount;
          const titleOrCode = title || code;
          return (
            <li
              key={titleOrCode}
              className="text-bd-primary ml-2 flex w-full justify-between px-1 font-light"
            >
              <p className="w-auto">{renderDiscount(discount)}</p>
              <p className="w-auto text-right">-{renderMoney(discountedAmount)}</p>
            </li>
          );
        })}
      </ul>
    </li>
  );
};

const CartLinesBreakdown = ({
  serviceLines,
  jewelries,
  looseBondedDiamonds,
  looseInStockDiamonds,
  others,
  cart,
}: {
  serviceLines: CartItem[];
  jewelries: { jewelry: CartItem; diamonds: CartItem[] }[];
  looseBondedDiamonds: CartItem[];
  looseInStockDiamonds: CartItem[];
  others: CartItem[];
  cart: Cart;
}) => {
  return (
    <div className="mt-6">
      <h2 className="font-medium text-slate-900">Items</h2>
      <ul className="mt-3 flex flex-col space-y-2 text-sm">
        {serviceLines.map((line) => (
          <CartLineBreakdown key={line.id} line={line} />
        ))}
        {jewelries.map((jewelry) => (
          <React.Fragment key={jewelry.jewelry.id}>
            <CartLineBreakdown line={jewelry.jewelry} />
            {jewelry.diamonds.map((diamond) => (
              <CartLineBreakdown key={diamond.id} line={diamond} />
            ))}
          </React.Fragment>
        ))}
        {looseBondedDiamonds.map((diamond) => (
          <CartLineBreakdown key={diamond.id} line={diamond} />
        ))}
        {looseInStockDiamonds.map((diamond) => (
          <CartLineBreakdown key={diamond.id} line={diamond} />
        ))}
        {others.map((other) => (
          <CartLineBreakdown key={other.id} line={other} />
        ))}
      </ul>

      <div className="border-bd-contrast mt-2 flex items-center justify-between border-t pt-2">
        <dt className="text-bd-dark/80 text-sm">Product Total</dt>
        <dd className="text-text-bd-dark text-sm font-medium">
          {renderMoney(cart.cost.nonDiscountedSubtotalAmount)}
        </dd>
      </div>

      {parseFloat(cart.cost.lineLevelDiscountAmount.amount) > 0 && (
        <div className="mt-2 flex items-center justify-between">
          <dt className="text-bd-primary text-sm">Product Discount</dt>
          <dd className="text-bd-primary text-sm font-medium">
            -{renderMoney(cart.cost.lineLevelDiscountAmount)}
          </dd>
        </div>
      )}

      <div className="border-bd-contrast mt-2 flex items-center justify-between border-t pt-2">
        <dt className="text-bd-dark/80 text-sm">Subtotal</dt>
        <dd className="text-text-bd-dark text-sm font-medium">
          {renderMoney(cart.cost.subtotalAmount)}
        </dd>
      </div>
      {cart.cost.paymentPlanName && (
        <div className="border-bd-contrast mt-2 flex items-center justify-between border-t pt-2">
          <dt className="text-bd-dark/80 text-sm">{cart.cost.paymentPlanName}</dt>
          <dd className="text-text-bd-dark text-sm font-medium">
            {renderMoney(cart.cost.todayPaymentAmount)}
          </dd>
        </div>
      )}
    </div>
  );
};

const displayByCode = {
  PAY_ONLY_DEPOSIT: `${rPercentage(1 - DIAMOND_DUE_RATIO)} due at final shipment`,
  PAY_ONLY_DEPOSIT_GOLD: `${rPercentage(1 - DIAMOND_DUE_RATIO)} due at final shipment`,
  PAY_ONLY_REMAINDER: `${rPercentage(DIAMOND_DUE_RATIO)} deposit`,
};
const DiscountBreakdown = ({ cart }: { cart: Cart }) => {
  const cartLevelDiscounts = getCartLevelDiscounts(cart);
  const cartLevelDiscountsTotal = getCartDiscountsTotal(cart);

  if (cartLevelDiscounts.length === 0) {
    return <></>;
  }
  return (
    <div className="mt-6">
      <h3 className="text-bd-dark text-base">Other deductions</h3>
      <ul className="mt-3 flex flex-col space-y-2 text-sm">
        {cartLevelDiscounts.map((discount) => (
          <li key={discount.title} className="flex w-full justify-between font-light">
            <p className="w-auto">
              {displayByCode[discount.title as keyof typeof displayByCode] || discount.title}
            </p>
            <p className="w-auto text-right">-{renderMoney(discount.totalAmount)}</p>
          </li>
        ))}
      </ul>

      <div className="border-bd-contrast mt-2 flex items-center justify-between border-t pt-2">
        <dt className="text-bd-primary text-sm">Deduction total</dt>
        <dd className="text-bd-primary text-sm font-medium">
          -{renderMoney(cartLevelDiscountsTotal)}
        </dd>
      </div>
      {/* 
      <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-6">
        <dt className="text-sm text-slate-600">After deduction total</dt>
        <dd className="text-sm font-medium text-slate-900">
          {renderMoney(subtractMoney(cart.cost.subtotalAmount, totalDiscount))}
        </dd>
      </div> */}
    </div>
  );
};

// const TwoPaymentPlan = ({ totalAmount }: { totalAmount: Money }) => {
//   return (
//     <div className="mt-6">
//       <h3 className="text-base text-slate-900">Two payment plan</h3>
//       <p className="text-sm text-slate-600">
//         <p className="text-sm text-slate-600">
//           {rPercentage(DIAMOND_DUE_RATIO)} (
//           {renderMoney(multiplyMoney(totalAmount, DIAMOND_DUE_RATIO))}) of the quote total is due at
//           order confirmation, and the remaining {rPercentage(1 - DIAMOND_DUE_RATIO)} (
//           {renderMoney(multiplyMoney(totalAmount, 1 - DIAMOND_DUE_RATIO))}) is due at final
//           shipment.
//         </p>
//       </p>
//     </div>
//   );
// };

const PriceSummaryCheckout = ({
  cart,
  quoteCart,
  isQuote,
  serviceLines,
  jewelries,
  looseBondedDiamonds,
  looseInStockDiamonds,
  others,
}: {
  cart: Cart;
  quoteCart?: Cart;
  isQuote: boolean;
  serviceLines: CartItem[];
  jewelries: { jewelry: CartItem; diamonds: CartItem[] }[];
  looseBondedDiamonds: CartItem[];
  looseInStockDiamonds: CartItem[];
  others: CartItem[];
}) => {
  const { status: sessionStatus } = useSession();

  const orderType = getCartOrderType(cart);
  const [quoteCartSaved, setQuoteCartSaved] = useState(true);
  const [canApplyDepositDiscountCode, setCanApplyDepositDiscountCode] = useState(true);

  const [addPayIn4Error, addPayIn4Action, pendingAddPayIn4Action] = useActionState(async () => {
    const result = await applySpread4MonthsPlanAction(cart);
    if (result.error) {
      return result.error;
    }
    return null;
  }, null);

  const [removePayIn4Error, removePayIn4Action, pendingRemovePayIn4Action] = useActionState(
    async () => {
      const result = await applyDepositDiscountCodeAction(cart);
      if (result.error) {
        return result.error;
      }
      return null;
    },
    null
  );

  // First time checkout, save the quote cart.
  useEffect(() => {
    const callSaveQuoteCart = async (cart: Cart, quoteCart: Cart | undefined) => {
      if (orderType === ORDER_TYPE_SERVICE && quoteCart) {
        setQuoteCartSaved(false);
        try {
          await saveQuoteCart(cart, quoteCart);
          setQuoteCartSaved(true);
        } catch (e) {
          console.error(e);
        }
      }
    };
    callSaveQuoteCart(cart, quoteCart);
  }, [orderType, quoteCart, cart, setQuoteCartSaved]);

  // Confirmation checkout, apply the discount code.
  useEffect(() => {
    const callApplyDepositDiscountCode = async (cart: Cart) => {
      const sellingPlanApplied = hasSpread4MonthsSellingPlan(cart);
      const depositDiscountCodeApplied = hasDepositDiscountCode(cart);
      if (sellingPlanApplied || depositDiscountCodeApplied || !canApplyDepositDiscountCode) {
        return;
      }
      if (orderType === ORDER_TYPE_CONFIRMATION) {
        const result = await applyDepositDiscountCodeAction(cart);
        setCanApplyDepositDiscountCode(false); // only apply once
        if (result.error) {
          console.error(result.error);
        }
      }
    };
    callApplyDepositDiscountCode(cart);
  }, [orderType, cart, canApplyDepositDiscountCode, setCanApplyDepositDiscountCode]);

  if (isCartEmpty(cart)) {
    return <></>;
  }

  return (
    <section
      aria-labelledby="summary-heading"
      className="bg-bd-secondary rounded-lg px-4 py-6 sm:p-6 lg:p-8"
    >
      <h2 id="summary-heading" className="text-xl font-medium capitalize">
        {isQuote ? 'Quote summary' : "Today's payment"}
      </h2>
      <CartLinesBreakdown
        serviceLines={serviceLines}
        jewelries={jewelries}
        looseBondedDiamonds={looseBondedDiamonds}
        looseInStockDiamonds={looseInStockDiamonds}
        others={others}
        cart={cart}
      />

      <DiscountBreakdown cart={cart} />

      <dl className="mt-2 space-y-2">
        <div className="border-bd-contrast flex items-center justify-between border-t pt-2">
          <dt className="text-bd-dark/80 flex items-center text-sm">
            <span>Shipping</span>
            {/* <a href="#" className="ml-2 shrink-0 text-slate-400 hover:text-slate-500">
              <span className="sr-only">Learn more about how shipping is calculated</span>
              <QuestionMarkCircleIcon aria-hidden="true" className="h-5 w-5" />
            </a> */}
          </dt>
          <dd className="text-text-bd-dark text-sm font-medium">Free</dd>
        </div>
        <div className="border-bd-contrast flex items-center justify-between border-t pt-2">
          <dt className="text-bd-dark/80 flex text-sm">
            <span>Tax estimate</span>
            {/* <a href="#" className="ml-2 shrink-0 text-slate-400 hover:text-slate-500">
              <span className="sr-only">Learn more about how tax is calculated</span>
              <QuestionMarkCircleIcon aria-hidden="true" className="h-5 w-5" />
            </a> */}
          </dt>
          <dd className="text-text-bd-dark text-sm font-medium">
            {cart && parseFloat(cart.cost.totalTaxAmount.amount) > 0 && !isQuote
              ? renderMoney(cart.cost.totalTaxAmount)
              : 'TBD'}
          </dd>
        </div>
        <div className="border-bd-contrast flex items-center justify-between border-t pt-2">
          <dt className="text-text-bd-dark text-base font-medium">
            {isQuote ? 'Quote total' : 'Payment today'}
          </dt>
          <dd className="text-text-bd-dark text-base font-medium">
            {cart &&
              renderMoney(isQuote ? cart.cost.afterDiscountSubtotalAmount : cart.cost.totalAmount)}
          </dd>
        </div>
      </dl>

      {isQuote ? (
        <>
          <div className="mt-6">
            <p className="text-bd-dark/80 text-sm">
              This quote will be saved for your reference. No commitment until you confirm your
              purchase.
            </p>
          </div>
          <section aria-labelledby="policies-heading" className="mt-10">
            <h2 id="policies-heading" className="text-xl font-medium capitalize">
              Payment plans
            </h2>

            <dl className="mt-4 grid grid-cols-1 gap-6">
              <PayIn2Plan totalAmount={cart.cost.afterDiscountSubtotalAmount} />
              <PayIn4Plan totalAmount={cart.cost.afterDiscountSubtotalAmount} />
            </dl>
          </section>
        </>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {!isFirstOrderCart(cart) &&
            (hasSpread4MonthsSellingPlan(cart) ? (
              <form action={removePayIn4Action}>
                <BDButton
                  styleType="secondary"
                  className="mx-auto w-full px-4 py-2"
                  type="submit"
                  disabled={pendingRemovePayIn4Action}
                  pending={pendingRemovePayIn4Action}
                >
                  Pay 50% Upfront
                </BDButton>
                {removePayIn4Error ? (
                  <p className="text-bd-primary text-sm">{removePayIn4Error}</p>
                ) : null}
              </form>
            ) : (
              <form action={addPayIn4Action}>
                <BDButton
                  styleType="secondary"
                  className="mx-auto w-full px-4 py-2"
                  type="submit"
                  disabled={pendingAddPayIn4Action}
                  pending={pendingAddPayIn4Action}
                >
                  Pay with 4 Easy Installments
                </BDButton>
                {addPayIn4Error ? (
                  <p className="text-bd-primary text-sm">{addPayIn4Error}</p>
                ) : null}
              </form>
            ))}
          <BDButton
            key="checkout"
            styleType="primary"
            className="mx-auto w-full px-4 py-2"
            href={`${cart?.checkoutUrl}${sessionStatus === 'authenticated' ? '?logged_in=true' : ''}`}
            disabled={!cart || !quoteCartSaved}
          >
            Checkout
            {!quoteCartSaved && <Spinner className="h-4 w-4" />}
          </BDButton>

          {!isFirstOrderCart(cart) && (
            <section aria-labelledby="policies-heading" className="mt-10">
              <h2 id="policies-heading" className="text-xl font-medium capitalize">
                Payment plans
              </h2>
              <dl className="mt-4 grid grid-cols-1 gap-6">
                <PayIn2PlanRaw />
                <PayIn4PlanRaw />
              </dl>
            </section>
          )}
        </div>
      )}
    </section>
  );
};

export default PriceSummaryCheckout;
